import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Payment } from '../models/process-payment-request.interface';
import { FinancialResponsibleContact } from '../models/contact.interface';

/**
 * Interface representing the overall state of the payment process.
 */
export interface PaymentProcessState extends Payment {
  entity: string | null;
  contact: FinancialResponsibleContact | null;
}

/**
 * PaymentProcessService is an Angular service that manages the state of a payment process.
 * It uses RxJS BehaviorSubject to maintain and expose the state as an observable.
 */
@Injectable({
  providedIn: 'root', // This makes the service available throughout the application and supports tree-shaking.
})
export class PaymentProcessService {
  /**
   * The initial state of the payment process.
   * This state is used to initialize the BehaviorSubject.
   */
  private initialState: PaymentProcessState = {
    amount: null,
    customer_id: null,
    office_id: null,
    payment_method: null,
    entity: null,
    contact: null,
    source_id: null,
    source_type: null,
  };

  /**
   * A BehaviorSubject to hold the current state of the payment process.
   * BehaviorSubject is used because it maintains the latest state and emits it immediately to new subscribers.
   */
  private stateSubject: BehaviorSubject<PaymentProcessState> =
    new BehaviorSubject<PaymentProcessState>(this.initialState);

  /**
   * Public observable that exposes the state. Consumers can subscribe to this observable to react to state changes.
   * This ensures that the internal state cannot be directly mutated from outside the service.
   */
  readonly state$: Observable<PaymentProcessState> = this.stateSubject.asObservable();

  /**
   * Subject to trigger the generation of a token.
   */
  private triggerGenerateTokenSubject: Subject<void> = new Subject<void>();

  /**
   * Public observable that exposes the triggerGenerateToken subject.
   */
  readonly triggerGenerateToken$: Observable<void> =
    this.triggerGenerateTokenSubject.asObservable();

  /**
   * Updates a specific field in the payment process state.
   * @param key - The key of the field to update.
   * @param value - The new value for the field.
   *  useage : this.paymentProcessService.updateField('amount', 100);
   */
  updateField<K extends keyof PaymentProcessState>(key: K, value: PaymentProcessState[K]): void {
    this.stateSubject.next({ ...this.stateSubject.value, [key]: value });
  }

  /**
   * Triggers the generation of a token.
   */
  triggerGenerateToken(): void {
    this.triggerGenerateTokenSubject.next();
  }

  clearPaymentState(): void {
    this.stateSubject.next(this.initialState);
  }
}
